import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, Heart } from "lucide-react";
import { MinimalShell } from "../components/layout/MinimalShell";
import { LeadForm } from "../components/shared/LeadCapture";
import { LOVE_YOUR_LIFE } from "../data/siteContent";

const reveal = (reduce, delay = 0) => ({
  initial: { opacity: 0, y: reduce ? 0 : 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: reduce ? 0 : 0.65, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function LoveYourLife() {
  const reduce = useReducedMotion();

  return (
    <MinimalShell>
      <section data-testid="love-your-life-page" className="relative overflow-hidden pt-32 md:pt-40 pb-20 grain">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <motion.p {...reveal(reduce)} className="text-sm tracking-[0.2em] uppercase text-[#E7CC86] mb-5">
            Love Your Life!
          </motion.p>
          <motion.h1 {...reveal(reduce, 0.08)} className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream">
            {LOVE_YOUR_LIFE.title}
          </motion.h1>
          <motion.p {...reveal(reduce, 0.16)} className="mt-7 max-w-3xl mx-auto font-serif italic text-2xl md:text-3xl leading-tight text-[#E7CC86]">
            {LOVE_YOUR_LIFE.deck}
          </motion.p>
          <motion.a {...reveal(reduce, 0.24)} href="#love-your-life-form" data-testid="love-your-life-cta" className="btn-gold mt-10 px-8 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2">
            Request workshop details <ArrowDown size={18} aria-hidden="true" />
          </motion.a>
        </div>
      </section>

      <section className="relative py-16 md:py-24 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 space-y-12 text-lg md:text-xl text-cream/80 leading-relaxed">
          <div className="space-y-6">
            {LOVE_YOUR_LIFE.introduction.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>

          <div className="gold-hairline" />

          <div className="space-y-6">
            {LOVE_YOUR_LIFE.workshop.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>

          <div className="rounded-2xl bg-[#080B12]/55 p-6 md:p-9">
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-tight text-cream mb-7">{LOVE_YOUR_LIFE.processHeading}</h2>
            <ol className="space-y-5">
              {LOVE_YOUR_LIFE.process.map((step, index) => (
                <li key={step} className="flex gap-4 items-start">
                  <span className="mt-1 w-8 h-8 shrink-0 rounded-full border border-[#C9A24B]/65 text-[#E7CC86] text-sm font-semibold flex items-center justify-center">{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="space-y-6">
            <p>{LOVE_YOUR_LIFE.practicesIntro}</p>
            <ul className="space-y-3 pl-6 list-disc marker:text-[#E7CC86]">
              {LOVE_YOUR_LIFE.practices.map((practice) => <li key={practice}>{practice}</li>)}
            </ul>
          </div>

          <blockquote className="py-7 px-6 md:px-10 text-center border-y border-[#C9A24B]/25">
            <p className="font-serif italic text-2xl md:text-4xl leading-snug text-[#E7CC86]">{LOVE_YOUR_LIFE.quote}</p>
          </blockquote>

          <div className="space-y-6">
            {LOVE_YOUR_LIFE.closing.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
        </div>
      </section>

      <section id="love-your-life-form" data-testid="love-your-life-form-section" className="relative py-20 md:py-28 overflow-hidden scroll-mt-20">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-xl mx-auto px-5 md:px-8">
          <div className="glass rounded-2xl p-7 md:p-10">
            <Heart size={22} className="text-[#E7CC86] mb-5" aria-hidden="true" />
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-tight text-cream mb-3">Request workshop details</h2>
            <p className="text-cream/70 mb-7">Stay close to this intimate weekend with Gary and Hope.</p>
            <LeadForm
              source={LOVE_YOUR_LIFE.source}
              avatar={LOVE_YOUR_LIFE.avatar}
              pathway={LOVE_YOUR_LIFE.pathway}
              interest={LOVE_YOUR_LIFE.interest}
              cta="Request workshop details"
            />
          </div>
        </div>
      </section>
    </MinimalShell>
  );
}
